import { apiClient } from '../utils/api';

export interface InAppNotificationItem {
  id: string;
  type: string;
  title: string;
  body: string;
  payload: Record<string, unknown> | null;
  read: boolean;
  readAt: string | null;
  createdAt: string;
}

export interface InAppFeedResult {
  data: InAppNotificationItem[];
  total: number;
  unreadCount: number;
}

async function parseJsonSafe(res: Response): Promise<any> {
  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) return null;
  return await res.json();
}

export class NotificationsApi {
  async inboxFeed(query?: { page?: number; limit?: number; read?: boolean }): Promise<InAppFeedResult> {
    const qs = new URLSearchParams();
    if (query?.page !== undefined) qs.set('page', String(query.page));
    if (query?.limit !== undefined) qs.set('limit', String(query.limit));
    if (query?.read !== undefined) qs.set('read', String(query.read));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';

    const res = await apiClient(`/api/notifications/inbox/feed${suffix}`, { method: 'GET' }, true);
    const data = await parseJsonSafe(res);
    if (!res.ok) {
      const err = new Error(data?.message || 'Failed to fetch inbox feed');
      (err as any).status = res.status;
      throw err;
    }
    return data?.data ?? { data: [], total: 0, unreadCount: 0 };
  }

  async unreadCount(): Promise<number> {
    const res = await apiClient('/api/notifications/inbox/unread-count', { method: 'GET' }, true);
    const data = await parseJsonSafe(res);
    if (!res.ok) return 0;
    return data?.data?.unreadCount ?? 0;
  }

  async mark(messageId: string, markAs: 'read' | 'unread') {
    const res = await apiClient(
      '/api/notifications/inbox/mark',
      { method: 'POST', body: JSON.stringify({ messageId, markAs }) },
      true,
    );
    const data = await parseJsonSafe(res);
    if (!res.ok) {
      const err = new Error(data?.message || 'Failed to update message state');
      (err as any).status = res.status;
      throw err;
    }
    return data;
  }

  async markAll(markAs: 'read') {
    const res = await apiClient(
      '/api/notifications/inbox/mark-all',
      { method: 'POST', body: JSON.stringify({ markAs }) },
      true,
    );
    const data = await parseJsonSafe(res);
    if (!res.ok) {
      const err = new Error(data?.message || 'Failed to update messages state');
      (err as any).status = res.status;
      throw err;
    }
    return data;
  }
}

export const notificationsApi = new NotificationsApi();
